import { Link, NavLink } from 'react-router-dom';
import EnvToggle from './EnvToggle';
import SettingsMenu from './SettingsMenu';
import WalletButton from './WalletButton';
import WsStatusDot from './WsStatusDot';
import HelpMenu from '../tour/HelpMenu';
import { useT } from '../i18n/useT';
import type { PlainKey } from '../i18n/i18n';
import { PRODUCT_NAME } from '../config/brand';
import { useMediaQuery } from '../catalog/useMediaQuery';

/** Tailwind's md: the nav moves up into the header row. */
const MD_UP = '(min-width: 768px)';

/** The name with its top-level domain in the accent, like the logo ("outcomes" warm white, ".lol" purple; #1546). */
function BrandName() {
  const dot = PRODUCT_NAME.lastIndexOf('.');
  if (dot <= 0) return <>{PRODUCT_NAME}</>;
  return (
    <>
      {PRODUCT_NAME.slice(0, dot)}
      <span className="text-accent">{PRODUCT_NAME.slice(dot)}</span>
    </>
  );
}

const NAV:{ to: string; label: PlainKey; end: boolean }[] = [
  { to: '/', label: 'nav.markets', end: true },
  { to: '/portfolio', label: 'nav.portfolio', end: false },
  { to: '/learn', label: 'nav.learn', end: false },
];

function NavItems({ className = '' }: { className?: string }) {
  const t = useT();
  return (
    <nav aria-label={t('shell.nav.label')} className={`flex items-center gap-1 ${className}`}>
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          className={({ isActive }) =>
            `rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              isActive ? 'bg-surface-2 text-fg' : 'text-muted hover:text-fg'
            }`
          }
        >
          {t(n.label)}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Header() {
  const t = useT();
  // Settings + Help (#2242): beside the wallet from md; on phones on the nav row, where there is room, so the top row
  // still fits at 375px with a wallet or ghost chip. Rendered once, so ids and test ids stay unique.
  const mdUp = useMediaQuery(MD_UP);
  const menus = (
    <div className="flex items-center gap-2">
      <SettingsMenu />
      <HelpMenu />
    </div>
  );
  return (
    <header className="sticky top-0 z-20 border-b border-border bg-surface/95 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4 sm:px-6">
        {/* Brand (#1546): the mascot mark (design/outcomes-lol) plus the product name as live text, not artwork, so
            the placeholder name can change later. Below 420px only the mark shows, to keep the header on one row. */}
        <Link to="/" aria-label={t('shell.header.home')} data-testid="brand" className="flex shrink-0 items-center gap-2">
          <img src="/logo-mark.png" alt="" width={28} height={28} className="h-7 w-7" />
          <span
            translate="no"
            data-testid="brand-name"
            className="hidden text-lg font-semibold tracking-tight text-fg min-[420px]:inline"
          >
            <BrandName />
          </span>
        </Link>
        <NavItems className="ms-2 hidden md:flex" />
        <div className="ms-auto flex items-center gap-2 sm:gap-3">
          <WsStatusDot />
          <EnvToggle />
          {mdUp ? menus : null}
          <WalletButton />
        </div>
      </div>
      {/* Mobile (≤ md): nav on its own row so it fits at 390px. */}
      <div className="flex items-center justify-between gap-2 border-t border-border px-2 py-1 md:hidden">
        <NavItems />
        {mdUp ? null : menus}
      </div>
    </header>
  );
}
